import React, { useState, useEffect } from 'react';
import { Table, Tag, Modal, Dropdown, Popconfirm, Input, Select } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { MenuProps } from 'antd/lib/index';
import { useNavigate } from 'react-router-dom';
import { DotsThreeOutlineIcon, EyeIcon, PencilSimpleLineIcon } from '@phosphor-icons/react';
import UserEditContent from './UserEditModalContent';
import EditUserAssignAccountModalContent from './EditUserAssignAccount';
import { API_URL } from 'env';
import { toast } from 'react-toastify';
import type { User, RawUser } from 'types/user';

export type { User };

const UserAvatar = ({ user }: { user: User }) => (
    <div className="w-9 h-9 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center shrink-0">
        <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
            {user.name?.charAt(0).toUpperCase() || 'U'}
        </span>
    </div>
);

const UserAccountList = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState<User[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [loading, setLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [assignUser, setAssignUser] = useState<User | null>(null);

    async function fetchData(page = 1, limit = 10) {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}api/v1/be/users?page=${page}&limit=${limit}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });
            const result = await res.json();
            if (result.status) {
                const mapped: User[] = result.data.items.map((u: RawUser) => ({
                    id: u.id,
                    name: u.full_name_display || u.fullname || u.email || '-',
                    email: u.email,
                    phonenumber: u.phone || '',
                    status: u.is_active === 1 ? 'Active' : 'Inactive',
                    balance: 0,
                    lastLogin: '',
                    firstName: u.first_name || '',
                    lastName: u.last_name || '',
                    jobTitle: u.job_title || '',
                    country: u.country || '',
                    city: u.city || '',
                    address: u.address || '',
                    postalCode: u.postal_code || '',
                    avatar_url: u.avatar_url || '',
                }));
                setUsers(mapped);
                setPagination(prev => ({ ...prev, current: result.data.current_page, total: result.data.total }));
            }
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { fetchData(); }, []);

    async function deleteUser(userId: string) {
        try {
            const res = await fetch(`${API_URL}api/v1/be/user/${userId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });
            if (res.ok) {
                toast.success('Đã xóa người dùng');
                fetchData(pagination.current);
            } else {
                toast.error('Không thể xóa người dùng');
            }
        } catch {
            toast.error('Lỗi kết nối');
        }
    }

    const getDropdownItems = (user: User): MenuProps['items'] => [
        { key: 'role', label: 'Chỉnh sửa role', onClick: () => navigate('/role') },
        { key: 'assign', label: 'Tài khoản được gán', onClick: () => setAssignUser(user) },
        { type: 'divider' },
        {
            key: 'delete',
            danger: true,
            label: (
                <Popconfirm
                    title="Xóa người dùng?"
                    description="Thao tác này không thể hoàn tác."
                    onConfirm={() => deleteUser(user.id)}
                    okText="Xóa"
                    cancelText="Hủy"
                >
                    <span>Xóa người dùng</span>
                </Popconfirm>
            ),
        },
    ];

    const columns: ColumnsType<User> = [
        {
            title: 'Người dùng',
            key: 'user',
            render: (_, user) => (
                <div className="flex items-center gap-3">
                    <UserAvatar user={user} />
                    <div>
                        <p className="text-sm font-medium mb-0!">{user.name}</p>
                        <p className="text-xs text-gray-400 mb-0!">{user.id}</p>
                    </div>
                </div>
            ),
        },
        {
            title: 'Thông tin liên hệ',
            key: 'contact',
            render: (_, user) => (
                <div>
                    <p className="mb-0! text-sm">{user.email || '-'}</p>
                    <p className="mb-0! text-xs text-gray-400">{user.phonenumber || '-'}</p>
                </div>
            ),
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            render: (val: string) => (
                <Tag color={val === 'Active' ? 'green' : 'default'}>
                    {val === 'Active' ? 'Hoạt động' : val === 'Inactive' ? 'Không hoạt động' : '-'}
                </Tag>
            ),
        },
        {
            title: 'Đăng nhập cuối',
            dataIndex: 'lastLogin',
            render: (val: string) => val || '-',
        },
        {
            title: '',
            key: 'actions',
            width: 120,
            render: (_, user) => (
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => navigate(`/users/${user.id}`)}
                        className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900 transition-colors cursor-pointer"
                    >
                        <EyeIcon size={16} />
                    </button>
                    <button
                        onClick={() => setEditingUser(user)}
                        className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-yellow-50 dark:hover:bg-yellow-900 transition-colors cursor-pointer"
                    >
                        <PencilSimpleLineIcon size={16} />
                    </button>
                    <Dropdown menu={{ items: getDropdownItems(user) }} trigger={['click']}>
                        <button className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors cursor-pointer">
                            <DotsThreeOutlineIcon size={16} weight="fill" />
                        </button>
                    </Dropdown>
                </div>
            ),
        },
    ];

    const handleTableChange = (pag: TablePaginationConfig) => {
        fetchData(pag.current ?? 1, pag.pageSize ?? 10);
    };

    const filteredUsers = users.filter(u => {
        const matchSearch = !searchQuery || u.name.toLowerCase().includes(searchQuery.toLowerCase()) || u.email.toLowerCase().includes(searchQuery.toLowerCase());
        const matchStatus = statusFilter === 'all' || u.status.toLowerCase() === statusFilter;
        return matchSearch && matchStatus;
    });

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-3">
                <Input.Search
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Tìm kiếm theo tên, email..."
                    className="flex-1"
                    allowClear
                />
                <Select
                    value={statusFilter}
                    onChange={setStatusFilter}
                    style={{ width: 180 }}
                    getPopupContainer={() => document.body}
                    dropdownStyle={{ zIndex: 99999 }}
                    options={[
                        { value: 'all', label: 'Tất cả' },
                        { value: 'active', label: 'Hoạt động' },
                        { value: 'inactive', label: 'Không hoạt động' },
                    ]}
                />
            </div>

            <Table
                columns={columns}
                dataSource={filteredUsers}
                rowKey="id"
                loading={loading}
                scroll={{ x: 'max-content' }}
                rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showTotal: total => `Tổng ${total} người dùng`,
                    showSizeChanger: false,
                }}
                onChange={handleTableChange}
            />

            <Modal
                title="Chỉnh sửa người dùng"
                open={!!editingUser}
                onCancel={() => setEditingUser(null)}
                footer={null}
            >
                {editingUser && (
                    <UserEditContent
                        user={editingUser}
                        onCancel={() => setEditingUser(null)}
                        triggerUpdate={() => fetchData(pagination.current)}
                    />
                )}
            </Modal>

            <Modal
                title="Tài khoản được gán"
                open={!!assignUser}
                onCancel={() => setAssignUser(null)}
                footer={null}
                width={1000}
            >
                {assignUser && <EditUserAssignAccountModalContent userId={assignUser.id} />}
            </Modal>
        </div>
    );
};

export default UserAccountList;
